import constants from "constants/constants";
import { api, dataProvider } from "data";
import debounce from "lodash/debounce";
import moment from "moment";
import { useCallback, useState } from "react";
import { is_empty } from "utils/validations";
import { SEARCH_TYPES, SECTION_TYPES, SESSION_STORAGE_KEYS } from "./data";
import { formatRecipients } from "./modules/RecipientsSelector/RecipientsSelector.utils";
import { getRAUrlParams } from "utils/urlUtils";
import {
  generateSelectedBulkActionLabel,
  performBulkAction,
} from "features/BulkActions/utils/BulkActions.utils";
import { isRequestSuccessful } from "utils/Utils";
import { requestIdParam } from "features/BulkActions/BulkActions.constants";
import { apiMethods } from "data/api.constants";
import SessionStorageConstants from "constants/SessionStorage.constants";
import { logError } from "utils/error";

const useRecipients = () => {
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState();
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const [alreadySelectedEmails, setAlreadySelectedEmails] = useState([]);

  const [fetchingRecipients, setFetchingRecipients] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [recipientsDataCurrentPage, setRecipientsDataCurrentPage] = useState(1);
  const urlParams = getRAUrlParams();
  const requestId = urlParams.get(requestIdParam);

  const fetchRecipients = async (params) => {
    try {
      const { page, all_listings = false } = params ?? {};
      setRecipientsLoading(true);
      const recipientsData = await dataProvider.custom_request(
        api.email_sections,
        apiMethods.GET,
        {
          page: page ?? recipientsDataCurrentPage,
          ...(all_listings && { all_listings }),
        }
      );

      let allRecipients = [];
      if (isRequestSuccessful(recipientsData.status)) {
        const { user_listing: currentRecipients, total_pages } =
          recipientsData.data;
        allRecipients = formatRecipients(currentRecipients);
        setTotalPages(total_pages);
        sessionStorage.setItem(
          SESSION_STORAGE_KEYS.RECIPIENTS_DATA,
          JSON.stringify(currentRecipients)
        );
        sessionStorage.setItem(
          SESSION_STORAGE_KEYS.RECIPIENTS_TIMESTAMP,
          moment().toString()
        );
      }

      // getting selected recipients from table (all customers, webpage leads, etc) - start
      if (requestId) {
        const { data: bulkActionData, status } = await performBulkAction({
          requestId,
        });
        if (isRequestSuccessful(status)) {
          const selectionCount = bulkActionData?.recipient_count;
          const table = bulkActionData?.crm_table;
          const displayName = `Selected ${generateSelectedBulkActionLabel({
            table,
            count: selectionCount,
          })}`;
          const bulkActionRecipientOption = [
            {
              section_type: SECTION_TYPES.BULK_ACTION_RECIPIENT,
              key: requestId,
              display_name: displayName,
              user_count: selectionCount,
            },
          ];
          const bulkSelectedRecipient = {
            uuid: requestId, // in default
            user_type: SECTION_TYPES.BULK_ACTION_RECIPIENT,
            display_name: displayName,
            user_count: selectionCount,
          };
          allRecipients = [...bulkActionRecipientOption, ...allRecipients];
          setSelectedRecipients([bulkSelectedRecipient]);
        }
      } else {
        let recipientList = sessionStorage.getItem(
          SessionStorageConstants.RECIPIENT_LIST
        );

        if (!is_empty(recipientList)) {
          const alreadySelectedRecipients = JSON.parse(recipientList)[0];
          const { emails: selectedEmails } = alreadySelectedRecipients;
          setAlreadySelectedEmails(selectedEmails);
          sessionStorage.removeItem(SessionStorageConstants.RECIPIENT_LIST);
        }
      }
      setRecipients(allRecipients);
      // getting selected recipients from table (all customers, webpage leads, etc) - end
    } catch (error) {
      logError({
        error,
        when: "initializing recipients data",
        occuredAt:
          "src/ui/pages/communications/CustomEmail/ComposeEmail/useRecipients.js",
        severity: "blocker",
        customErrorMessage: error.message || constants.error_message,
      });
    } finally {
      setRecipientsLoading(false);
    }
  };

  const getAllLeadsAndAllCustomers = () => {
    if (is_empty(recipients)) return [];

    const allLeads = recipients.find(
      ({ section_type }) => section_type === SECTION_TYPES.ALL_LEADS
    );
    const allCustomers = recipients.find(
      ({ section_type }) => section_type === SECTION_TYPES.ALL_CUSTOMERS
    );

    return [
      {
        ...allLeads,
        title: allLeads.display_name,
        type: SEARCH_TYPES.ALL_LEADS,
      },
      {
        ...allCustomers,
        title: allCustomers.display_name,
        type: SEARCH_TYPES.ALL_CUSTOMERS,
      },
    ];
  };

  const fetchMoreRecipients = async () => {
    if (recipientsDataCurrentPage >= totalPages) return;
    if (fetchingRecipients) return;

    const page = recipientsDataCurrentPage + 1;

    try {
      setFetchingRecipients(true);
      const recipientsData = await dataProvider.custom_request(
        api.email_sections,
        "GET",
        {
          page,
        }
      );

      if (isRequestSuccessful(recipientsData.status)) {
        const { user_listing: newRecipients } = recipientsData.data;
        const allRecipients = [
          ...recipients,
          ...formatRecipients(newRecipients),
        ];
        setRecipients(allRecipients);
        setRecipientsDataCurrentPage(page);
      }
    } catch (error) {
      logError({
        error,
        when: "calling fetchMoreRecipients",
        occuredAt:
          "src/ui/pages/communications/CustomEmail/ComposeEmail/useRecipients.js",
        severity: "blocker",
        customErrorMessage: error?.message || constants.error_message,
      });
    } finally {
      setFetchingRecipients(false);
    }
  };

  const searchRecipient = useCallback(
    debounce(async (search_string) => {
      if (!search_string) return setSearchResult([]);

      try {
        const response = await dataProvider.custom_request(
          api.email_suggestions,
          "GET",
          {
            search_field: search_string,
            get_count: "True",
          }
        );
        const { search_result } = response.data;
        const additionalData = search_string?.toLowerCase().includes("all")
          ? getAllLeadsAndAllCustomers()
          : [];
        setSearchResult([
          ...additionalData,
          ...formatRecipients(search_result),
        ]);
      } catch (error) {
        console.error(error.message);
      }
    }, 500),
    [recipients]
  );

  const clearResult = () => {
    if (!is_empty(searchResult)) setSearchResult([]);
  };

  return {
    fetchingRecipients,
    fetchMoreRecipients,
    recipients,
    alreadySelectedEmails,
    searchRecipient,
    searchResult,
    clearResult,
    selectedRecipients,
    setSelectedRecipients,
    recipientsLoading,
    fetchRecipients,
  };
};

export default useRecipients;
